import Icon from '../Icon';
// src/components/Editor/EditorUI.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import Nav from '../SidePanel/Nav';
import Tooltip from '../Overlays/Tooltip';
import Modal from '../Overlays/Modal';
import ProjectPanel from '../ProjectPanel/ProjectPanel';
import ChatsPanel from '../ChatsPanel/ChatsPanel';
import OutlinePanel from '../OutlinePanel/OutlinePanel';
import VersionPanel from '../VersionPanel/VersionPanel';
import TabShelf from '../TabShelf/TabShelf';
import EditorPanel from '../EditorPanel/EditorPanel';
import OptionsPanel from '../OptionsPanel/OptionsPanel';
import { saveDocContent, updateDocLayout, buildDocFromContent, openDoc, createDoc } from '../../helpers/docHelpers';
import { deleteVersion, getVersionContent, getVersionDisplay, buildVersionDocId } from '../../helpers/versionHelpers';
import { closeTab } from '../../helpers/tabHelpers';
import { uuid } from '../../helpers/uuid';
import { notifyToast } from '../../helpers/notificationHelpers';
import { docMapKey, folderMapKey, findItem } from '../../helpers/mapKey';
import { computeOpenBehavior } from '../../helpers/navHelpers';
import { apiLoadDoc } from '../../api/docs';

export default function EditorUI(props) {
  const {
    user,
    currentProject,
    projectReadOnly,
    activePanel,
    setActivePanel,
    isMobile,
    projectPinned,
    outlinePinned,
    versionsPinned,
    chatsPinned,
    setProjectPinned,
    setOutlinePinned,
    setVersionsPinned,
    setChatsPinned,
    tabs,
    setTabs,
    activeTabId,
    setActiveTabId,
    selectedDocId,
    setSelectedDocId,
    editorPanelRef,
    rightEditorPanelRef,
    onEditorChange,
    optionsOpen,
    handleToggleOptions,
    optionsPinned,
    setOptionsPinned,
    itemsArr,
    itemsMap,
    setItemsMap,
    dragStart,
    dragOver,
    dragEnd,
    dropOnItem,
    dropInProject,
    dropInTabs,
    dropInEditor,
    draggedId,
    handleClickOutside,
    activeEditor,
    splitScreen,
    setSplitScreen,
    leftActiveTabId,
    setLeftActiveTabId,
    rightActiveTabId,
    setRightActiveTabId,
    activePane,
    setActivePane,
    revealEditorChrome = () => {},
    hideEditorChrome = false,
    tabsRestored,
    refresh,
    showShareButton = false,
    onShareClick
  } = props;

  const isLeftPanelOpen = ['projectButton', 'outlineButton', 'versionsButton', 'chatsButton'].includes(activePanel);
  const leftPanelFixed = activePanel === 'projectButton'
    ? projectPinned
    : activePanel === 'chatsButton'
    ? chatsPinned
    : activePanel === 'outlineButton'
    ? outlinePinned
    : activePanel === 'versionsButton'
    ? versionsPinned
    : false;
  const [restoreConfirm, setRestoreConfirm] = useState({
    isOpen: false,
    version: null,
    targetName: 'current document',
    originTabId: null
  });
  const [isRestoreConfirming, setIsRestoreConfirming] = useState(false);
  const [versionsRefreshToken, setVersionsRefreshToken] = useState(0);
  const [chatsRefreshToken, setChatsRefreshToken] = useState(0);
  const [inlineRenameRequest, setInlineRenameRequest] = useState(null);
  const restoreVersionLabel = getVersionDisplay(restoreConfirm?.version || null).label;

  const activePinState = activePanel === 'projectButton'
    ? projectPinned
    : activePanel === 'chatsButton'
    ? chatsPinned
    : activePanel === 'outlineButton'
    ? outlinePinned
    : activePanel === 'versionsButton'
    ? versionsPinned
    : false;

  const handleTogglePin = () => {
    if (['projectButton', 'chatsButton', 'outlineButton', 'versionsButton'].includes(activePanel)) {
      const nextPinned = !activePinState;
      setProjectPinned(nextPinned);
      setChatsPinned(nextPinned);
      setOutlinePinned(nextPinned);
      setVersionsPinned(nextPinned);
    }
  };

  const versionsPanelOpen = activePanel === 'versionsButton';
  const chatsPanelOpen = activePanel === 'chatsButton';

  const activePaneTabId = activePane === 'right' ? rightActiveTabId : leftActiveTabId;
  const activeTab = tabs.find(t => t.id === activePaneTabId);
  const notifyActivePaneToast = (input, options = {}) => {
    const baseWhere = options?.where && typeof options.where === 'object' ? options.where : {};
    const pane = splitScreen
      ? (activeTab?.pane === 'right' ? 'right' : (activeTab?.pane === 'left' ? 'left' : (activePane === 'right' ? 'right' : 'left')))
      : null;
    const resolvedDocId = activeTab?.isVersion ? activeTab?.sourceDocId : activeTab?.docId;

    notifyToast(input, {
      ...options,
      where: pane
        ? {
            ...baseWhere,
            ...(resolvedDocId != null ? { docId: baseWhere.docId ?? resolvedDocId, scope: baseWhere.scope || 'doc' } : {}),
            pane: baseWhere.pane || pane
          }
        : baseWhere
    });
  };

  const resolveDocOutcomeWhere = (docId, where = {}) => {
    const baseWhere = where && typeof where === 'object' ? where : {};
    const resolvedDocId = docId ?? baseWhere.docId ?? null;
    const sourceTab = resolvedDocId
      ? tabs.find(t => !t.isVersion && String(t.docId) === String(resolvedDocId))
      : null;
    const pane = sourceTab?.pane === 'right' ? 'right' : (sourceTab?.pane === 'left' ? 'left' : null);

    if (resolvedDocId == null) {
      return baseWhere;
    }

    return {
      ...baseWhere,
      docId: resolvedDocId,
      ...(pane ? { pane: baseWhere.pane || pane } : {})
    };
  };

  const notifySourceDocToast = (sourceDocId, input, options = {}) => {
    notifyToast(input, {
      ...options,
      where: resolveDocOutcomeWhere(sourceDocId, options?.where)
    });
  };

  const resolveItemName = (docId) => {
    if (!docId || !itemsMap) return null;
    if (typeof itemsMap.get === 'function') {
      return itemsMap.get(docMapKey(docId))?.name ?? null;
    }
    return itemsMap[docMapKey(docId)]?.name ?? null;
  };

  const sourceDocId = activeTab?.isVersion ? activeTab.sourceDocId : activeTab?.docId;
  const focusedDocName = resolveItemName(sourceDocId) || activeTab?.label;
  const focusedDoc = sourceDocId ? { id: sourceDocId, name: focusedDocName } : null;
  const isActiveTabVersion = Boolean(activeTab?.isVersion);

  const normalizeTabPane = (paneName) => (paneName === 'right' ? 'right' : 'left');

  const focusExistingTab = (tab) => {
    if (!tab) return;
    const targetPane = normalizeTabPane(tab.pane);
    if (targetPane === 'right') setRightActiveTabId?.(tab.id);
    else setLeftActiveTabId?.(tab.id);
    setActivePane?.(targetPane);
    setActiveTabId?.(tab.id);
    setSelectedDocId?.(tab.docId || null);
  };

  const ensureTabPaneAssignment = (docId, targetPane) => {
    setTabs(prev =>
      prev.map(t =>
        t.docId === docId && (!t.pane || t.pane !== targetPane)
          ? { ...t, pane: targetPane }
          : t
      )
    );
  };

  const openDocInEditorPane = (docItem, mode = 'replace', preferredPane = null) => {
    if (!docItem) return;
    revealEditorChrome();
    const existingTab = tabs.find(t => t.docId === docItem.id);
    if (existingTab) {
      focusExistingTab(existingTab);
      return;
    }

    const targetPane = normalizeTabPane(preferredPane || activePane);
    if (targetPane === 'right' && !splitScreen) {
      setSplitScreen?.(true);
    }
    const paneActiveTabId = targetPane === 'right' ? rightActiveTabId : leftActiveTabId;
    const setPaneActiveTabId = targetPane === 'right' ? setRightActiveTabId : setLeftActiveTabId;
    const setFocusedTabId = (tabId) => {
      setPaneActiveTabId?.(tabId);
      setActiveTabId?.(tabId);
    };
    const paneTabs = targetPane === 'right'
      ? tabs.filter(t => t.pane === 'right')
      : tabs.filter(t => !t.pane || t.pane === 'left');
    const effectiveMode = mode === 'new' || paneTabs.length === 0 ? 'new' : mode;

    openDoc(docItem, tabs, setTabs, paneActiveTabId, setFocusedTabId, editorPanelRef, effectiveMode);
    if (effectiveMode === 'new') ensureTabPaneAssignment(docItem.id, targetPane);
    setActivePane?.(targetPane);
  };

  const handleOpenChat = (chatDoc, mode = 'replace', preferredPane = null) => {
    if (!chatDoc?.id) return;
    const docItem = {
      id: chatDoc.id,
      name: chatDoc.name || 'Untitled Chat',
      type: 'doc',
      docType: 'chat',
      readOnly: true
    };

    openDocInEditorPane(docItem, mode, preferredPane);
  };

  const handleCreateChat = async () => {
    if (!currentProject?.id || !user?.id || projectReadOnly) return;

    const newChat = await createDoc(
      currentProject.id,
      null,
      user.id,
      itemsMap,
      setItemsMap,
      'chat'
    );

    if (!newChat?.id) return;

    const docItem = {
      ...newChat,
      type: 'doc',
      docType: 'chat',
      readOnly: true
    };

    let preferredPane = activePane;
    try {
      const beh = computeOpenBehavior({ type: 'chat', tabs, activePane, leftActiveTabId, rightActiveTabId });
      preferredPane = beh?.preferredPane || preferredPane;
    } catch (err) {
      // Keep current pane when preference lookup fails.
    }

    setActivePanel?.('chatsButton');
    openDocInEditorPane(docItem, 'new', preferredPane);
    setChatsRefreshToken(prev => prev + 1);
  };

  const handleOpenSearchResult = async (result) => {
    const resultDocId = String(result?.doc_id ?? '').trim();
    if (!resultDocId) return;

    let foundDoc = null;
    if (typeof itemsMap?.values === 'function') {
      for (const value of itemsMap.values()) {
        if (value?.type === 'doc' && String(value.id) === resultDocId) {
          foundDoc = value;
          break;
        }
      }
    }

    let resolvedDocType = String(
      result?.doc_type ??
      result?.type ??
      foundDoc?.docType ??
      ''
    ).trim().toLowerCase() || null;

    if (!resolvedDocType) {
      try {
        const loadedDoc = await apiLoadDoc(resultDocId);
        resolvedDocType = String(loadedDoc?.type || '').trim().toLowerCase() || null;
      } catch (err) {
        resolvedDocType = null;
      }
    }

    if (foundDoc && resolvedDocType === 'chat' && foundDoc.docType !== 'chat') {
      foundDoc = {
        ...foundDoc,
        docType: 'chat',
        readOnly: true
      };
    }

    const docItem = foundDoc || {
      id: result?.doc_id,
      name: result?.doc_name || 'Untitled',
      type: 'doc',
      docType: resolvedDocType === 'chat' ? 'chat' : null,
      readOnly: resolvedDocType === 'chat'
    };

    const openType = resolvedDocType === 'chat' ? 'chat' : 'doc';
    let mode = 'replace';
    let preferredPane = activePane;

    try {
      const beh = computeOpenBehavior({ type: openType, tabs, activePane, leftActiveTabId, rightActiveTabId });
      mode = beh?.mode || 'replace';
      preferredPane = beh?.preferredPane || activePane;
    } catch (err) {
      mode = 'replace';
      preferredPane = activePane;
    }

    openDocInEditorPane(docItem, mode, preferredPane);
  };

  const handleAutoVersionCreated = ({ docId }) => {
    if (!versionsPanelOpen) return;
    if (String(focusedDoc?.id ?? '') !== String(docId ?? '')) return;
    setVersionsRefreshToken(prev => prev + 1);
  };

  const handleOpenVersion = async (version) => {
    if (!version?.versionId || !currentProject?.id || !focusedDoc?.id) {
      throw new Error('Missing version context');
    }

    const sourceDocId = version?.sourceDocId || focusedDoc.id;
    const requestedPane = version?.preferredPane === 'right' ? 'right' : (version?.preferredPane === 'left' ? 'left' : null);
    const anyVersionTab = tabs.find(t => t?.isVersion) || null;
    const versionSlot =
      tabs.find(t => t?.isVersion && t?.id === activePaneTabId) ||
      tabs.find(t => t?.isVersion && t?.pane === 'right') ||
      anyVersionTab;

    const shouldForceFirstVersionToRight = !anyVersionTab;
    if (shouldForceFirstVersionToRight && !splitScreen) {
      setSplitScreen?.(true);
    }

    const existingTab = tabs.find(
      t => t.isVersion && t.versionId === version.versionId && t.sourceDocId === sourceDocId
    );

    if (existingTab) {
      const targetPane = requestedPane || (shouldForceFirstVersionToRight ? 'right' : (existingTab.pane === 'right' ? 'right' : 'left'));
      if (existingTab.pane !== targetPane) {
        setTabs(prev => prev.map(t => (t.id === existingTab.id ? { ...t, pane: targetPane } : t)));
      }

      if (targetPane === 'right') {
        setRightActiveTabId?.(existingTab.id);
      } else {
        setLeftActiveTabId?.(existingTab.id);
      }
      setActivePane?.(targetPane);
      setActiveTabId?.(existingTab.id);
      setSelectedDocId?.(existingTab.docId);
      return;
    }

    const targetPane = requestedPane || (shouldForceFirstVersionToRight
      ? 'right'
      : (versionSlot?.pane === 'right' ? 'right' : 'left'));
    const versionDocId = buildVersionDocId(sourceDocId, version.versionId);
    const versionDisplay = getVersionDisplay({
      versionId: version.versionId,
      savedAt: version.savedAt
    }).label;
    const resolvedLabel = version?.sourceDocName || resolveItemName(sourceDocId) || focusedDoc.name || 'Document';

    const versionTabPayload = {
      docId: versionDocId,
      label: `${resolvedLabel} · ${versionDisplay}`,
      readOnly: true,
      toolbarVariant: 'version',
      pane: targetPane,
      isVersion: true,
      versionId: version.versionId,
      versionSavedAt: version.savedAt || null,
      versionMetadata: version.metadata || null,
      sourceDocId
    };

    const targetVersionTab = versionSlot
      ? { ...versionSlot, ...versionTabPayload }
      : { id: `version-tab-${uuid()}`, ...versionTabPayload };

    setTabs(prev => {
      if (!versionSlot) return [...prev, targetVersionTab];
      return prev.map(t => (t.id === versionSlot.id ? targetVersionTab : t));
    });

    if (targetPane === 'right') {
      setRightActiveTabId?.(targetVersionTab.id);
    } else {
      setLeftActiveTabId?.(targetVersionTab.id);
    }
    setActivePane?.(targetPane);
    setActiveTabId?.(targetVersionTab.id);
    setSelectedDocId?.(targetVersionTab.docId);
  };

  const getItemById = (itemId) => {
    if (!itemId || !itemsMap) return null;
    if (typeof itemsMap.get === 'function') {
      return findItem(itemsMap, itemId) ?? null;
    }
    return itemsMap[docMapKey(itemId)] || itemsMap[folderMapKey(itemId)] || null;
  };

  const resolveVersionContext = (version) => {
    const sourceDocId = version?.sourceDocId || focusedDoc?.id;
    if (!sourceDocId) throw new Error('Missing source doc id');
    const sourceItem = getItemById(sourceDocId);
    return {
      sourceDocId,
      sourceItem,
      sourceName: sourceItem?.name || focusedDoc?.name || 'Document'
    };
  };

  const handleDeleteVersion = async (version) => {
    if (!currentProject?.id) throw new Error('Missing project context');
    const { sourceDocId } = resolveVersionContext(version);

    await deleteVersion({
      projectId: currentProject.id,
      docId: sourceDocId,
      versionId: version.versionId
    });

    // Close the version tab if it's currently open
    const versionDocId = buildVersionDocId(sourceDocId, version.versionId);
    const versionTab = tabs.find(t => t.docId === versionDocId);
    if (versionTab) {
      closeVersionTabById(versionTab.id);
    }

    notifySourceDocToast(sourceDocId, 'Version deleted');
  };

  const executeRestoreVersion = async (version) => {
    if (!currentProject?.id || !user?.id) throw new Error('Missing restore context');
    const { sourceDocId } = resolveVersionContext(version);

    const payload = await getVersionContent({
      projectId: currentProject.id,
      docId: sourceDocId,
      versionId: version.versionId
    });

    const versionDoc = payload.doc || {};
    const contentToSave = versionDoc.content;
    if (!contentToSave) throw new Error('Version content is empty');

    await saveDocContent(sourceDocId, contentToSave, user.id, null, currentProject.id);
    if (versionDoc.layout) {
      await updateDocLayout(sourceDocId, versionDoc.layout);
    }

    editorPanelRef?.current?.updateDocContent?.(
      sourceDocId,
      contentToSave,
      versionDoc.updated_at || payload.savedAt || new Date().toISOString()
    );

    notifySourceDocToast(sourceDocId, 'Version restored');
  };

  const handleRestoreVersion = async (version) => {
    const originTabId = version?.originTabId || null;
    const { sourceName } = resolveVersionContext(version);
    setRestoreConfirm({
      isOpen: true,
      version,
      targetName: sourceName || 'current document',
      originTabId
    });
  };

  const closeVersionTabById = (tabId) => {
    if (!tabId) return;
    const tabToClose = tabs.find(t => t.id === tabId);
    if (!tabToClose) return;

    const pane = tabToClose.pane === 'right' ? 'right' : 'left';
    const paneTabs = pane === 'left'
      ? tabs.filter(t => !t.pane || t.pane === 'left')
      : tabs.filter(t => t.pane === 'right');
    const paneActiveTabId = pane === 'left' ? leftActiveTabId : rightActiveTabId;
    const paneResult = closeTab(paneTabs, paneActiveTabId, tabId);
    const remainingTabs = tabs.filter(t => t.id !== tabId);
    const remainingRightTabs = remainingTabs.filter(t => t.pane === 'right');

    setTabs(remainingTabs);

    if (pane === 'left') {
      setLeftActiveTabId?.(paneResult.activeTabId);
    } else {
      setRightActiveTabId?.(paneResult.activeTabId);
      if (splitScreen && remainingRightTabs.length === 0) {
        setSplitScreen?.(false);
        setActivePane?.('left');
      }
    }

    const wasFocusedTab = activePane === pane && paneActiveTabId === tabId;
    if (wasFocusedTab) {
      setActiveTabId?.(paneResult.activeTabId);
      setSelectedDocId?.(
        paneResult.activeTabId
          ? remainingTabs.find(t => t.id === paneResult.activeTabId)?.docId || null
          : null
      );
    } else if (remainingTabs.length === 0) {
      setActiveTabId?.(null);
      setSelectedDocId?.(null);
    }
  };

  const handleConfirmRestoreVersion = async () => {
    if (!restoreConfirm?.version) return;
    setIsRestoreConfirming(true);
    try {
      await executeRestoreVersion(restoreConfirm.version);
      if (restoreConfirm.originTabId) {
        closeVersionTabById(restoreConfirm.originTabId);
      }
      setRestoreConfirm({ isOpen: false, version: null, targetName: 'current document', originTabId: null });
    } catch (err) {
      const sourceDocId = restoreConfirm?.version?.sourceDocId || null;
      notifySourceDocToast(sourceDocId, err?.message || 'Unable to restore version.');
    } finally {
      setIsRestoreConfirming(false);
    }
  };

  const handleRestoreVersionAsCopy = async (version) => {
    if (!currentProject?.id || !user?.id) throw new Error('Missing restore context');
    const { sourceDocId, sourceItem, sourceName } = resolveVersionContext(version);

    const payload = await getVersionContent({
      projectId: currentProject.id,
      docId: sourceDocId,
      versionId: version.versionId
    });

    const versionDoc = payload.doc || {};
    const contentToSave = versionDoc.content;
    if (!contentToSave) throw new Error('Version content is empty');

    const parentId = sourceItem?.parent_id ?? null;
    const restoredName = `${sourceName} (Restored)`;
    const restoredDoc = await buildDocFromContent({
      projectId: currentProject.id,
      parentId,
      userId: user.id,
      name: restoredName,
      content: contentToSave,
      layout: versionDoc.layout,
      itemsMap,
      setItemsMap
    });
    if (!restoredDoc?.id) throw new Error('Failed to create restored copy');

    if (typeof refresh === 'function') {
      try {
        await refresh();
      } catch (err) {
        // no-op: local optimistic map is already patched
      }
    }

    setActivePanel?.('projectButton');
    setInlineRenameRequest({
      docId: restoredDoc.id,
      name: restoredName,
      requestedAt: Date.now()
    });

    const originTabId = version?.originTabId || null;
    const originTab = originTabId ? tabs.find(t => t.id === originTabId) : null;
    const sourceTab = tabs.find(t => !t.isVersion && String(t.docId) === String(sourceDocId));
    const sourcePane = sourceTab?.pane === 'right' ? 'right' : 'left';
    const targetPane = sourcePane === 'right' ? 'left' : 'right';

    setSplitScreen?.(true);

    if (originTab?.isVersion && originTab.pane === targetPane) {
      const replacementTab = {
        id: originTab.id,
        docId: restoredDoc.id,
        label: restoredName,
        pane: targetPane,
        readOnly: false,
        toolbarVariant: null
      };

      setTabs(prev => prev.map(t => (t.id === originTab.id ? replacementTab : t)));
      if (targetPane === 'right') {
        setRightActiveTabId?.(originTab.id);
      } else {
        setLeftActiveTabId?.(originTab.id);
      }
      setActivePane?.(targetPane);
      setActiveTabId?.(originTab.id);
      setSelectedDocId?.(restoredDoc.id);
    } else {
      const newTab = {
        id: `restored-tab-${uuid()}`,
        docId: restoredDoc.id,
        label: restoredName,
        pane: targetPane,
        readOnly: false,
        toolbarVariant: null
      };

      setTabs(prev => [...prev, newTab]);
      if (targetPane === 'right') {
        setRightActiveTabId?.(newTab.id);
      } else {
        setLeftActiveTabId?.(newTab.id);
      }
      setActivePane?.(targetPane);
      setActiveTabId?.(newTab.id);
      setSelectedDocId?.(restoredDoc.id);
    }

    editorPanelRef?.current?.loadExternalDoc?.(restoredDoc.id, {
      content: contentToSave,
      layout: versionDoc.layout ?? null,
      name: restoredName,
      updated_at: versionDoc.updated_at || payload.savedAt || new Date().toISOString()
    });

    notifyActivePaneToast('Restored copy created', {
      where: {
        docId: restoredDoc.id,
        pane: targetPane
      }
    });
  };

  const handleRestoreVersionFromToolbar = async (tab) => {
    if (!tab?.isVersion || !tab?.versionId || !tab?.sourceDocId) return;
    await handleRestoreVersion({
      versionId: tab.versionId,
      sourceDocId: tab.sourceDocId,
      sourceDocName: resolveItemName(tab.sourceDocId) || 'Document',
      savedAt: tab.versionSavedAt,
      metadata: tab.versionMetadata,
      originTabId: tab.id
    });
  };

  const handleRestoreVersionCopyFromToolbar = async (tab) => {
    if (!tab?.isVersion || !tab?.versionId || !tab?.sourceDocId) return;
    await handleRestoreVersionAsCopy({
      versionId: tab.versionId,
      sourceDocId: tab.sourceDocId,
      sourceDocName: resolveItemName(tab.sourceDocId) || 'Document',
      savedAt: tab.versionSavedAt,
      metadata: tab.versionMetadata,
      originTabId: tab.id
    });
  };

  // Memoize filtered tabs to prevent infinite re-renders
  const leftTabs = useMemo(() => tabs.filter(t => !t.pane || t.pane === 'left'), [tabs]);
  const rightTabs = useMemo(() => tabs.filter(t => t.pane === 'right'), [tabs]);
  
  // Memoize selectedDocIds
  const leftSelectedDocId = useMemo(() => {
    if (leftActiveTabId) {
      const tab = tabs.find(t => t.id === leftActiveTabId);
      if (tab?.docId) return tab.docId;
    }
    // Fallback to selectedDocId (used in non-split mode or when leftActiveTabId lookup fails)
    return selectedDocId;
  }, [leftActiveTabId, tabs, selectedDocId]);

  const rightSelectedDocId = useMemo(() => {
    if (rightActiveTabId) {
      const tab = tabs.find(t => t.id === rightActiveTabId);
      return tab?.docId;
    }
    return null;
  }, [rightActiveTabId, tabs]);

  // Left panel resizer state (allow widening the left panel)
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    try {
      const v = window.localStorage.getItem('leftPanelWidth');
      return v ? Number(v) : null;
    } catch (e) {
      return null;
    }
  });
  const leftResizerRef = useRef(null);
  const leftDragging = useRef(false);
  const leftMinRef = useRef(null);
  // Split editor dragging (between left and right editor panes)
  const [editorSplitPercent, setEditorSplitPercent] = useState(50);
  const splitDragging = useRef(false);
  const editorContainerRef = useRef(null);
  const setGlobalResizeCursor = () => {
    try {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } catch (e) {}
  };
  const clearGlobalResizeCursor = () => {
    try {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    } catch (e) {}
  };
  const leftPaneStyle = splitScreen ? { width: `${editorSplitPercent}%` } : undefined;
  const rightPaneStyle = splitScreen
    ? { width: `${100 - editorSplitPercent}%` }
    : undefined;

  useEffect(() => {
    // Initialize from computed style if not present
    try {
      if (leftPanelWidth == null && typeof document !== 'undefined') {
        const el = document.getElementById('leftPanel');
        if (el) {
          const w = parseInt(window.getComputedStyle(el).width, 10) || 240;
          setLeftPanelWidth(w);
        }
      }
    } catch (e) {}
  }, [leftPanelWidth]);

  useEffect(() => {
    const onMove = (e) => {
      if (leftDragging.current) {
        const min = leftMinRef.current || (leftPanelWidth || 240);
        const max = Math.max(360, Math.floor(window.innerWidth * 0.6));
        const newWidth = Math.max(min, Math.min(max, e.clientX));
        setLeftPanelWidth(newWidth);
        setGlobalResizeCursor();
      }

      if (splitDragging.current) {
        const el = editorContainerRef.current || document.getElementById('editorContainer');
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = Math.max(rect.left + 40, Math.min(rect.right - 40, e.clientX));
        const percent = Math.round(((x - rect.left) / rect.width) * 100);
        setEditorSplitPercent(Math.max(10, Math.min(90, percent)));
        setGlobalResizeCursor();
      }
    };
    const onUp = () => {
      if (leftDragging.current) {
        leftDragging.current = false;
        try { window.localStorage.setItem('leftPanelWidth', String(leftPanelWidth)); } catch (e) {}
      }
      if (splitDragging.current) {
        splitDragging.current = false;
      }
      if (!leftDragging.current && !splitDragging.current) clearGlobalResizeCursor();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      clearGlobalResizeCursor();
    };
  }, [leftPanelWidth]);

  const startLeftDrag = (e) => {
    e.preventDefault();
    leftDragging.current = true;
    // Fixed minimum width for left panel (exclude nav width): 240px
    leftMinRef.current = 240;
    setGlobalResizeCursor();
  };

  const startSplitDrag = (e) => {
    e.preventDefault();
    splitDragging.current = true;
    setGlobalResizeCursor();
  };

  useEffect(() => {
    // Reset split percent when splitScreen toggles off to default 50
    if (!splitScreen) {
      setEditorSplitPercent(50);
    }
  }, [splitScreen]);

  if (process.env.NODE_ENV !== 'production') {
    const componentRegistry = {
      Nav,
      Tooltip,
      ProjectPanel,
      ChatsPanel,
      OutlinePanel,
      VersionPanel,
      TabShelf,
      EditorPanel,
      OptionsPanel
    };

    Object.entries(componentRegistry).forEach(([name, component]) => {
      if (!component) {
        // eslint-disable-next-line no-console
        console.error(`[EditorUI] Component import is undefined: ${name}`);
      }
    });
  }

  return (
    <div id="app" className={hideEditorChrome ? 'editor-chrome-hidden' : ''} onClick={handleClickOutside}>
      <Tooltip />
      <Nav activePanel={activePanel} setActivePanel={setActivePanel} user={user} activeTabId={activeTabId} tabs={tabs} navMode={projectReadOnly ? 'viewer' : 'full'} showShareButton={!projectReadOnly && showShareButton} onShareClick={onShareClick} />

      <div 
        id="leftPanel" 
        className={`${isMobile ? 'overlay' : (leftPanelFixed ? 'fixed' : 'overlay')} ${isLeftPanelOpen ? 'open' : ''}`}
        style={leftPanelWidth ? { width: `${leftPanelWidth}px`, ...(isMobile || !leftPanelFixed ? {} : { position: 'relative' }) } : undefined}
      >
        <ProjectPanel
          user={user}
          project={currentProject}
          projectReadOnly={projectReadOnly}
          tabs={tabs}
          setTabs={setTabs}
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          leftActiveTabId={leftActiveTabId}
          rightActiveTabId={rightActiveTabId}
          activePane={activePane}
          setActivePane={setActivePane}
          setLeftActiveTabId={setLeftActiveTabId}
          setRightActiveTabId={setRightActiveTabId}
          setSplitScreen={setSplitScreen}
          inlineRenameRequest={inlineRenameRequest}
          editorRef={editorPanelRef}
          fixed={isMobile ? false : projectPinned}
          itemsArr={itemsArr}
          itemsMap={itemsMap}
          setItemsMap={setItemsMap}
          refresh={refresh}
          dragStart={dragStart}
          dragOver={dragOver}
          dragEnd={dragEnd}
          dropOnItem={dropOnItem}
          dropInProject={dropInProject}
          className={activePanel === 'projectButton' ? '' : 'hidden'}
        />
        <ChatsPanel
          fixed={isMobile ? false : chatsPinned}
          className={activePanel === 'chatsButton' ? '' : 'hidden'}
          projectId={currentProject?.id}
          projectName={currentProject?.name}
          activeDoc={focusedDoc}
          chatsPanelOpen={chatsPanelOpen}
          refreshToken={chatsRefreshToken}
          onOpenChat={handleOpenChat}
          onCreateChat={handleCreateChat}
          onOpenSearchResult={handleOpenSearchResult}
          projectReadOnly={projectReadOnly}
          tabs={tabs}
          setTabs={setTabs}
          setActiveTabId={setActiveTabId}
          dragStart={dragStart}
          dragEnd={dragEnd}
          activePane={activePane}
          leftActiveTabId={leftActiveTabId}
          rightActiveTabId={rightActiveTabId}
          user={user}
          itemsMap={itemsMap}
          setItemsMap={setItemsMap}
        />
        <OutlinePanel 
          fixed={isMobile ? false : outlinePinned}
          className={activePanel === 'outlineButton' ? '' : 'hidden'}
        />
        <VersionPanel
          fixed={isMobile ? false : versionsPinned}
          className={activePanel === 'versionsButton' ? '' : 'hidden'}
          projectId={currentProject?.id}
          projectName={currentProject?.name}
          activeDoc={focusedDoc}
          refreshToken={versionsRefreshToken}
          tabs={tabs}
          leftActiveTabId={leftActiveTabId}
          rightActiveTabId={rightActiveTabId}
          activePane={activePane}
          versionsPanelOpen={versionsPanelOpen}
          onOpenVersion={handleOpenVersion}
          onDeleteVersion={handleDeleteVersion}
          onRestoreVersion={handleRestoreVersion}
          onRestoreVersionCopy={handleRestoreVersionAsCopy}
        />
        <div id="leftPanelFooter">
          <button
            className={`iconButton medium`}
            onClick={handleTogglePin}
            aria-label={activePinState ? 'Unpin panel' : 'Pin panel'}
            data-tooltip={activePinState ? 'Unpin panel' : 'Pin panel'}
            data-tooltip-placement="top"
            disabled={!['projectButton', 'chatsButton', 'outlineButton', 'versionsButton'].includes(activePanel)}
          >
            <Icon icon={activePinState ? 'push-pin' : 'push-pin-slash'} className="icon medium" />
          </button>
        </div>
        <div ref={leftResizerRef} className="left-panel-resizer" onMouseDown={startLeftDrag} />
      </div>

      <div id="main">
        <TabShelf
          tabs={tabs}
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          setTabs={setTabs}
          setSelectedDocId={setSelectedDocId}
          onToggleOptions={handleToggleOptions}
          optionsOpen={optionsOpen}
          itemsMap={itemsMap}
          setItemsMap={setItemsMap}
          dragStart={dragStart}
          dragEnd={dragEnd}
          dropInTabs={dropInTabs}
          draggedId={draggedId}
          splitScreen={splitScreen}
          setSplitScreen={setSplitScreen}
          leftPercent={editorSplitPercent}
          leftActiveTabId={leftActiveTabId}
          setLeftActiveTabId={setLeftActiveTabId}
          rightActiveTabId={rightActiveTabId}
          setRightActiveTabId={setRightActiveTabId}
          activePane={activePane}
          setActivePane={setActivePane}
          currentProject={currentProject}
          user={user}
        />

        <div id="editorContainer" ref={editorContainerRef} className={splitScreen ? 'split' : ''}>
          <div className="editor-pane editor-pane-left" style={leftPaneStyle}>
            <EditorPanel
            ref={editorPanelRef}
            tabs={leftTabs}
            allTabs={tabs}
            setTabs={setTabs}
            activeTabId={leftActiveTabId || (splitScreen ? null : activeTabId)}
            setActiveTabId={setActiveTabId}
            currentUserId={user?.id}
            selectedDocId={leftSelectedDocId}
            onEditorChange={onEditorChange}
            dropInEditor={dropInEditor}
            draggedId={draggedId}
            className={`${splitScreen ? 'editor-left' : ''} ${activePane === 'left' ? 'focused' : ''}`.trim()}
            pane="left"
            setActivePane={setActivePane}
            currentProject={currentProject}
            itemsMap={itemsMap}
            setItemsMap={setItemsMap}
            setSplitScreen={setSplitScreen}
            setLeftActiveTabId={setLeftActiveTabId}
            setRightActiveTabId={setRightActiveTabId}
            leftActiveTabId={leftActiveTabId}
            rightActiveTabId={rightActiveTabId}
            tabsRestored={tabsRestored}
            isProjectReadOnly={projectReadOnly}
            onRestoreVersion={handleRestoreVersionFromToolbar}
            onRestoreVersionCopy={handleRestoreVersionCopyFromToolbar}
            onVersionCreated={handleAutoVersionCreated}
            onOpenVersion={handleOpenVersion}
          />
          </div>

          {splitScreen && (
            <>
              <div
                className="editor-split-resizer"
                onMouseDown={startSplitDrag}
                role="separator"
                aria-orientation="vertical"
                style={{ left: `${editorSplitPercent}%` }}
              />
              <div className="editor-pane editor-pane-right" style={rightPaneStyle}>
                <EditorPanel
                  ref={rightEditorPanelRef}
              tabs={rightTabs}
              allTabs={tabs}
              setTabs={setTabs}
              activeTabId={rightActiveTabId}
              setActiveTabId={setActiveTabId}
              currentUserId={user?.id}
              selectedDocId={rightSelectedDocId}
                onEditorChange={onEditorChange}
                dropInEditor={dropInEditor}
                draggedId={draggedId}
                className={`editor-right ${activePane === 'right' ? 'focused' : ''}`.trim()}
                pane="right"
                setActivePane={setActivePane}
                currentProject={currentProject}
                itemsMap={itemsMap}
                setItemsMap={setItemsMap}
                setSplitScreen={setSplitScreen}
                setLeftActiveTabId={setLeftActiveTabId}
                setRightActiveTabId={setRightActiveTabId}
                leftActiveTabId={leftActiveTabId}
                rightActiveTabId={rightActiveTabId}
                tabsRestored={tabsRestored}
                isProjectReadOnly={projectReadOnly}
                onRestoreVersion={handleRestoreVersionFromToolbar}
                onRestoreVersionCopy={handleRestoreVersionCopyFromToolbar}
                onVersionCreated={handleAutoVersionCreated}
                onOpenVersion={handleOpenVersion}
              />

                </div>
            </>

          )}
        </div>
      </div>

      <OptionsPanel
        editor={activeEditor}
        open={optionsOpen}
        onClose={() => handleToggleOptions(false)}
        fixed={isMobile ? false : optionsPinned}
        onToggleFixed={() => setOptionsPinned(prev => !prev)}
        docId={selectedDocId}
        editorRef={editorPanelRef}
        isVersionDoc={isActiveTabVersion}
      />

      <Modal
        isOpen={restoreConfirm.isOpen}
        title="Restore Document?"
        confirmText={isRestoreConfirming ? 'Restoring...' : 'Restore'}
        cancelText="Cancel"
        onConfirm={handleConfirmRestoreVersion}
        onCancel={() => {
          if (isRestoreConfirming) return;
          setRestoreConfirm({ isOpen: false, version: null, targetName: 'current document', originTabId: null });
        }}
        confirmDisabled={isRestoreConfirming}
      >
        <p>Restoring will replace all content in the current document:</p>
        <blockquote>{restoreConfirm.targetName}</blockquote>
        <p>With the content from this version:</p>
        <blockquote>{restoreVersionLabel}</blockquote>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
}
